import { useEffect, useMemo, useState } from 'react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { TextInput } from '../../../shared/components/TextInput';
import { useEstados, useMunicipios } from '../../geo/hooks';
import type { PersonalData } from '../api';
import { EDUCATION_OPTIONS, MARITAL_OPTIONS, SEX_OPTIONS, isBrazilian } from './format';

interface Props {
  value: PersonalData;
  onChange: (patch: Partial<PersonalData>) => void;
  /** UF da naturalidade já gravada, para hidratar o select. */
  initialUf?: string | null;
  errors?: Record<string, string>;
}

const NONE = { value: '', label: 'Não informado' };

/**
 * Dados civis da pessoa (seção 9.1). A naturalidade (UF + município IBGE)
 * só aparece para nacionalidade brasileira, mesma regra do backend.
 */
export function PersonalDataFields({ value, onChange, initialUf, errors = {} }: Props) {
  const [uf, setUf] = useState(initialUf ?? '');
  const estados = useEstados();
  const brasileira = isBrazilian(value.nationality);
  const municipios = useMunicipios(brasileira ? uf : null);

  useEffect(() => {
    if (initialUf) setUf(initialUf);
  }, [initialUf]);

  const cidades = useMemo(
    () => (municipios.data ?? []).slice().sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')),
    [municipios.data],
  );

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <TextInput label="Nome social" value={value.socialName ?? ''} onChange={(e) => onChange({ socialName: e.target.value })} />
      <TextInput label="Nome preferencial" value={value.preferredName ?? ''} onChange={(e) => onChange({ preferredName: e.target.value })} />
      <TextInput
        label="Data de nascimento"
        type="date"
        value={value.birthDate ?? ''}
        error={errors.birthDate}
        onChange={(e) => onChange({ birthDate: e.target.value || null })}
      />
      <AutocompleteSelect
        label="Estado civil"
        options={[NONE, ...MARITAL_OPTIONS]}
        value={value.maritalStatus ?? ''}
        onChange={(v) => onChange({ maritalStatus: v || null })}
      />
      <TextInput label="País de nascimento" value={value.birthCountry ?? ''} onChange={(e) => onChange({ birthCountry: e.target.value })} />
      <TextInput label="Nacionalidade" value={value.nationality ?? ''} onChange={(e) => onChange({ nationality: e.target.value })} />
      {brasileira && (
        <>
          <AutocompleteSelect
            label="UF de naturalidade"
            options={(estados.data ?? []).map((e) => ({ value: e.uf, label: `${e.uf} — ${e.nome}` }))}
            value={uf}
            onChange={(v) => {
              setUf(v);
              onChange({ birthCityId: null });
            }}
          />
          <AutocompleteSelect
            label="Município de naturalidade"
            options={cidades.map((m) => ({ value: String(m.idIbge), label: m.nome }))}
            value={value.birthCityId ? String(value.birthCityId) : ''}
            disabled={!uf}
            onChange={(v) => onChange({ birthCityId: v ? Number(v) : null })}
          />
        </>
      )}
      <AutocompleteSelect
        label="Sexo cadastral"
        options={[NONE, ...SEX_OPTIONS]}
        value={value.sex ?? ''}
        onChange={(v) => onChange({ sex: v || null })}
      />
      <AutocompleteSelect
        label="Escolaridade"
        options={[NONE, ...EDUCATION_OPTIONS]}
        value={value.educationLevel ?? ''}
        onChange={(v) => onChange({ educationLevel: v || null })}
      />
      <TextInput label="Nome da mãe" value={value.motherName ?? ''} onChange={(e) => onChange({ motherName: e.target.value })} />
      <TextInput label="Nome do pai" value={value.fatherName ?? ''} onChange={(e) => onChange({ fatherName: e.target.value })} />
    </div>
  );
}
